/* eslint-disable @typescript-eslint/no-unused-vars */
import { useConfigContext } from "client/components/configuration/ConfigContext"
import { Voting_Question } from "client/graphql"
import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react"

import { useChannelContext } from "../../../components/channel"
import { useActiveQuestion, useBatchedVote } from "../votingHooks"

type Props = {
  children: ReactNode
}

type VotingState = {
  activeQuestion: Voting_Question
  selectedAnswerId: string
  castVote: (answerId: string) => void
}

const VotingContext = createContext<VotingState | undefined>(undefined)

const VotingProvider: FC<Props> = ({ children }) => {
  const { channelId } = useChannelContext()
  const activeQuestion = useActiveQuestion(channelId)
  const config = useConfigContext()

  const [selectedAnswerId, setSelectedAnswerId] = useState<string | null>(
    null,
  )
  const castVoteEndpoint = config.api.pluginServiceVotingUrl
  const vote = useBatchedVote(channelId, castVoteEndpoint)

  useEffect(() => {
    setSelectedAnswerId(null)
  }, [activeQuestion])

  const castVote = (answerId: string) => {
    vote(activeQuestion.id, answerId)
    if (activeQuestion.maxVotesAllowed === 1) {
      setSelectedAnswerId(answerId)
    }
  }

  const votingState: VotingState = {
    activeQuestion,
    selectedAnswerId,
    castVote,
  }

  return (
    <VotingContext.Provider value={votingState}>
      {children}
    </VotingContext.Provider>
  )
}

const useVotingContext = () => {
  const votingContext = useContext(VotingContext)
  if (!votingContext) {
    throw new Error(
      "No VotingContext.Provider found when calling useVotingContext.",
    )
  }
  return votingContext
}

export { VotingContext, VotingProvider, useVotingContext }
